/**
 * Everything that differs between the live Service Plan outline and the Service Plan Template one.
 *
 * The two boards are the same board: the same tree, the same row renderer, the same bulk commands and
 * the same handlers behind them. What differs is which entities the rows are, which server commands
 * reorder and reparent them, which of the four bulk actions the definition can actually carry, and
 * what the parent record contributes to the schedule context. All of that lands here, and
 * `createOutlineRelationBuilder` reads nothing else.
 */
import { Injector } from '@angular/core';
import { ActivityEntityName, DayShortName, Entity, EntityLink, ServicePlanBlackoutRange } from '@jms/model';
import { ScheduleWeekday } from '@jms/model/scheduling/schedule.rule';
import { OutlineEntityKinds } from '../outline.authoring.model';

/**
 * Which bulk / Detail actions this outline offers.
 *
 * Each flag is a statement about the DEFINITION, not a preference. A template has no calendar, so a
 * blackout range — which names calendar days — has nothing to attach to; a template has no batch
 * reparent command on the server, so "Move to line" would present a picker with no write behind it.
 */
export interface OutlineRelationFeatures {
  /** Bulk and Detail "Set schedule…" on root tasks. On for both — a cadence is not a date. */
  schedule: boolean;
  /** Bulk and Detail "Move to line…". Needs a server batch-reparent command. */
  moveToLine: boolean;
  /** Bulk and Detail "Set crew…" on lines. */
  crew: boolean;
  /** Bulk and Detail "Set blackout…" on lines. Live only — a definition holds no calendar days. */
  blackout: boolean;
  /**
   * "No crew" chip on lines with neither a default crew nor a contractor. Live only: a template line is
   * staffed when a plan is made from it, so flagging every generic template line would be noise.
   */
  noCrew?: boolean;
}

/** The server commands this outline dispatches. */
export interface OutlineRelationCommands {
  /** One drag on the tree; the server routes it to a reorder or a reparent. */
  move: string;
  /** Batch reparent of tasks onto one line. Required when `features.moveToLine` is on. */
  reparent?: string;
}

/** What the parent record contributes: the panel's own copy, and the schedule context the rows draw against. */
export interface OutlineRelationRecordAccess<TRecord> {
  /** Board header parent, and the `parent` entry on the params bag. */
  parentEntity: ActivityEntityName;
  /** Extra params the create dialog needs alongside `parent` — `servicePlan` live, `servicePlanTemplate` on the template. */
  createLineParams: (record: TRecord, parent: Entity) => Record<string, unknown>;
  /** Extra initial values the create dialog seeds. */
  createLineInitialValues?: (record: TRecord, parent: Entity) => Record<string, unknown>;
  /** Whether the record locks its own catalogue — a Started plan, an archived template. */
  isCatalogueLocked: (record: TRecord) => boolean;
  timezone?: (record: TRecord) => string | undefined;
  workingDays?: (record: TRecord) => DayShortName[] | undefined;
  blackoutRanges?: (record: TRecord) => ServicePlanBlackoutRange[] | undefined;
  customer?: (record: TRecord) => Entity | undefined;
  site?: (record: TRecord) => Entity | undefined;
  /** The working week the bulk schedule dialog asks its questions against. */
  workWeek: (record: TRecord) => ScheduleWeekday[];
}

/** The board chrome that is genuinely per-outline. */
export interface OutlineRelationPanel {
  width: number;
  minWidth: number;
  maxWidth: number;
}

export interface OutlineRelationConfig<TRecord> {
  kinds: OutlineEntityKinds;
  features: OutlineRelationFeatures;
  commands: OutlineRelationCommands;
  record: OutlineRelationRecordAccess<TRecord>;
  panel: OutlineRelationPanel;
  headerTitle: string;
  emptyTitle: string;
  /** Display properties the list offers. `startsOn` is live-only — a definition has no start date. */
  displayProperties: string[];
  /** Copies an ordered checklist under the selected line or parent task. */
  addTasksFromChecklist?: (injector: Injector, parent: EntityLink, onSaved: (taskIds: string[]) => void | Promise<void>) => void;
  /**
   * The server-side view set this board renders, when it is not the unqualified one.
   *
   * The board resolves the view it RENDERS from its own header call, and that call carries no parent —
   * only the variant. Now that both outlines draw the SAME task entity, the variant is the one
   * parent-shaped fact that separates their view sets: without it a template outline would render the
   * live plan's view, however the outline call itself was answered.
   */
  variant?: string;
}

/** The panel geometry both outlines use; a difference here would be an accident, not a decision. */
export const OUTLINE_RELATION_PANEL: OutlineRelationPanel = { width: 440, minWidth: 360, maxWidth: 520 };

/** Display columns both outlines offer. Neither side lists `startsOn`: it is authored in the schedule editor. */
export const OUTLINE_RELATION_DISPLAY_PROPERTIES = ['schedule', 'duration', 'items', 'description', 'evidence', 'defaultResources'];
